"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

// ─── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  blurple: "#5865F2",
  blurpleGlow: "rgba(88,101,242,0.55)",
  surface: "#0a0b0f",
  card: "#0d0e14",
  cardRaised: "#11131a",
  text: "#E8E9F0",
  muted: "#6B6F85",
  faint: "#3A3D50",
  gold: "#FFD700",
  accentLine: "rgba(88,101,242,0.22)",
} as const;

// ─── Discord SVG logo ───────────────────────────────────────────────────────────
const DiscordLogo: React.FC<{ size?: number; className?: string }> = ({
  size = 52,
  className,
}) => (
  <svg
    width={size}
    height={size * 0.775}
    viewBox="0 0 71 55"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.401329 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978Z"
      fill="white"
    />
  </svg>
);

// ─── CodeChip ───────────────────────────────────────────────────────────────────
const CodeChip: React.FC<{ children: string }> = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(children).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [children]);

  return (
    <motion.code
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md font-mono text-[0.86em] cursor-pointer select-none"
      style={{
        background:
          "linear-gradient(135deg, rgba(88,101,242,0.18) 0%, rgba(114,137,218,0.12) 100%)",
        border: "1px solid rgba(88,101,242,0.45)",
        color: "#A5B4FC",
        letterSpacing: "0.02em",
      }}
      whileHover={{
        scale: 1.06,
        background:
          "linear-gradient(135deg, rgba(88,101,242,0.28) 0%, rgba(114,137,218,0.2) 100%)",
        transition: { type: "spring", stiffness: 400, damping: 18 },
      }}
      whileTap={{ scale: 0.94 }}
    >
      {children}
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.span
            key="check"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 20 }}
            style={{ color: "#4ADE80" }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6l3 3 5-5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.span>
        ) : (
          <motion.span
            key="copy"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 20 }}
            style={{ color: "#A5B4FC", opacity: 0.55 }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <rect
                x="4"
                y="1"
                width="7"
                height="9"
                rx="1.2"
                stroke="currentColor"
                strokeWidth="1.2"
              />
              <path
                d="M3 3H2a1 1 0 00-1 1v7a1 1 0 001 1h6a1 1 0 001-1v-1"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
          </motion.span>
        )}
      </AnimatePresence>
    </motion.code>
  );
};

// ─── GoldAmount ─────────────────────────────────────────────────────────────────
const GoldAmount: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <strong
    className="font-bold"
    style={{
      color: C.gold,
      fontSize: "1.1em",
      textShadow: "0 0 16px rgba(255,215,0,0.6), 0 0 40px rgba(255,215,0,0.25)",
    }}
  >
    {children}
  </strong>
);

// ─── Panel variants — tween, no stagger, no filter ───────────────────────────────
const panelVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.94 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "tween",
      duration: 0.26,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    y: 18,
    scale: 0.96,
    transition: { duration: 0.16, ease: [0.4, 0, 1, 1] },
  },
} as const;

// ─── Portal ─────────────────────────────────────────────────────────────────────
const ModalPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const elRef = useRef<HTMLDivElement | null>(null);
  if (!elRef.current) elRef.current = document.createElement("div");
  useEffect(() => {
    const el = elRef.current!;
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);
  return createPortal(children, elRef.current);
};

// ─── Props ───────────────────────────────────────────────────────────────────────
export interface DiscordLinkingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────────
export default function DiscordLinkingModal({
  isOpen,
  onClose,
  onConnect,
}: DiscordLinkingModalProps) {
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const content = (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="dlm-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 flex items-center justify-center p-6"
            style={{
              background:
                "radial-gradient(ellipse at 50% 40%, rgba(88,101,242,0.12) 0%, rgba(0,0,0,0.78) 60%)",
              zIndex: 5000002,
            }}
            onClick={onClose}
          >
            {/* Panel */}
            <motion.div
              key="dlm-panel"
              variants={prefersReduced ? undefined : panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-[480px] rounded-2xl overflow-hidden"
              style={{
                background: C.surface,
                willChange: "transform",
                boxShadow: [
                  "0 0 0 1px rgba(88,101,242,0.2)",
                  "0 50px 120px rgba(0,0,0,0.85)",
                  "0 0 120px rgba(88,101,242,0.12)",
                  "inset 0 1px 0 rgba(255,255,255,0.04)",
                ].join(", "),
              }}
            >
              {/* ── HERO HEADER ── */}
              <div
                className="relative px-8 pt-10 pb-8 flex flex-col items-center overflow-hidden"
                style={{
                  background:
                    "linear-gradient(180deg, #0d0f1a 0%, #0a0b0f 100%)",
                }}
              >
                {/* Radial bg glow */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(88,101,242,0.22) 0%, transparent 70%)",
                  }}
                />

                {/* Decorative grid lines */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.04]"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(88,101,242,1) 1px, transparent 1px), linear-gradient(90deg, rgba(88,101,242,1) 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                  }}
                />

                {/* Close button */}
                <motion.button
                  onClick={onClose}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-10"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}
                  whileHover={{
                    scale: 1.12,
                    rotate: 90,
                    background: "rgba(255,255,255,0.09)",
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 18,
                    },
                  }}
                  whileTap={{ scale: 0.88 }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M1 1l8 8M9 1L1 9"
                      stroke="rgba(255,255,255,0.45)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </motion.button>

                {/* Logo */}
                <div className="relative mb-6 z-10">
                  {/* Blurple halo disc */}
                  <div
                    className="absolute inset-[-10px] rounded-full"
                    style={{
                      background:
                        "radial-gradient(circle, rgba(88,101,242,0.28) 0%, transparent 70%)",
                      filter: "blur(8px)",
                    }}
                  />
                  <div
                    className="relative w-[72px] h-[72px] rounded-full flex items-center justify-center"
                    style={{
                      background:
                        "linear-gradient(145deg, #4752C4 0%, #5865F2 50%, #7289DA 100%)",
                      boxShadow: [
                        "0 0 0 1px rgba(114,137,218,0.4)",
                        "0 0 40px rgba(88,101,242,0.5)",
                        "0 8px 30px rgba(0,0,0,0.5)",
                        "inset 0 1px 0 rgba(255,255,255,0.18)",
                      ].join(", "),
                    }}
                  >
                    <DiscordLogo size={38} />
                  </div>
                </div>

                {/* Title */}
                <h2
                  className="bebas-neue-font text-[2.6rem] leading-[0.95] text-center z-10 mb-1"
                  style={{
                    color: "#ffffff",
                    letterSpacing: "0.01em",
                    textShadow:
                      "0 0 40px rgba(88,101,242,0.35), 0 2px 20px rgba(0,0,0,0.8)",
                  }}
                >
                  Conectează-te la{" "}
                  <span
                    style={{
                      color: C.blurple,
                      textShadow: `0 0 30px ${C.blurpleGlow}, 0 0 60px rgba(88,101,242,0.3)`,
                    }}
                  >
                    Discord
                  </span>
                </h2>

                {/* Subtitle */}
                <div className="flex items-center gap-2 mt-3 z-10">
                  <div
                    className="h-[1px] w-8"
                    style={{ background: C.accentLine }}
                  />
                  <span
                    className="chakra-petch-font text-[0.65rem] uppercase tracking-[0.18em]"
                    style={{ color: C.muted }}
                  >
                    Bonusuri exclusive · Membrii activi
                  </span>
                  <div
                    className="h-[1px] w-8"
                    style={{ background: C.accentLine }}
                  />
                </div>
              </div>

              {/* ── BODY ── */}
              <div
                className="px-7 pb-7 pt-5 flex flex-col gap-4"
                style={{ background: C.card }}
              >
                {/* Rich description card */}
                <div
                  className="relative rounded-xl p-5 overflow-hidden"
                  style={{
                    background: C.cardRaised,
                    border: "1px solid rgba(88,101,242,0.14)",
                  }}
                >
                  {/* Left accent bar */}
                  <div
                    className="absolute left-0 top-4 bottom-4 w-[3px] rounded-full"
                    style={{
                      background:
                        "linear-gradient(180deg, #5865F2 0%, #9B59B6 100%)",
                      boxShadow: "0 0 12px rgba(88,101,242,0.6)",
                    }}
                  />

                  <p
                    className="chakra-petch-font text-[0.6rem] uppercase tracking-[0.16em] mb-3 ml-4"
                    style={{ color: C.blurple, opacity: 0.85 }}
                  >
                    Cum funcționează
                  </p>

                  <p
                    className="gilroy-font !font-[500] leading-[1.75] text-[0.9rem] ml-4"
                    style={{ color: C.text }}
                  >
                    Dacă ai textul <CodeChip>atlantis.mp/discord</CodeChip> în
                    Status-ul tău Discord, vei primi un bonus de{" "}
                    <GoldAmount>$2,000</GoldAmount> la fiecare salariu. În
                    plus, ocazional acordăm{" "}
                    <span
                      style={{
                        background:
                          "linear-gradient(90deg, #A5B4FC, #7289DA, #9B59B6)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        fontWeight: 700,
                      }}
                    >
                      bonusuri mult mai mari
                    </span>{" "}
                    celor care au serverul nostru în descriere! Asigură-te că
                    îl ai și tu!
                  </p>
                </div>

                {/* Info chips */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { icon: "💰", label: "+$2,000", sub: "per salariu" },
                    { icon: "🎁", label: "Surprise", sub: "bonusuri" },
                    { icon: "⚡", label: "Instant", sub: "activ" },
                  ].map((chip, i) => (
                    <motion.div
                      key={i}
                      className="flex flex-col items-center gap-1.5 rounded-xl py-3.5 px-2"
                      style={{
                        background:
                          "linear-gradient(160deg, rgba(88,101,242,0.1) 0%, rgba(88,101,242,0.04) 100%)",
                        border: "1px solid rgba(88,101,242,0.16)",
                      }}
                      whileHover={{
                        background:
                          "linear-gradient(160deg, rgba(88,101,242,0.18) 0%, rgba(88,101,242,0.08) 100%)",
                        borderColor: "rgba(88,101,242,0.35)",
                        scale: 1.03,
                        transition: {
                          type: "spring",
                          stiffness: 400,
                          damping: 20,
                        },
                      }}
                    >
                      <span className="text-[1.35rem] leading-none">
                        {chip.icon}
                      </span>
                      <span
                        className="gilroy-font !font-[700] text-[0.8rem] leading-none"
                        style={{ color: "#ffffff" }}
                      >
                        {chip.label}
                      </span>
                      <span
                        className="chakra-petch-font text-[0.58rem] uppercase tracking-wide"
                        style={{ color: C.muted }}
                      >
                        {chip.sub}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* Divider */}
                <div
                  className="h-[1px]"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(88,101,242,0.25), rgba(155,89,182,0.2), transparent)",
                  }}
                />

                {/* CTA button */}
                <motion.button
                  onClick={onConnect}
                  className="relative w-full overflow-hidden rounded-xl py-4 px-5 flex items-center justify-center gap-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #3A44C4 0%, #5865F2 45%, #6B76F5 60%, #5865F2 78%, #3A44C4 100%)",
                    boxShadow: [
                      "0 0 0 1px rgba(114,137,218,0.4)",
                      "0 6px 30px rgba(88,101,242,0.45)",
                      "0 2px 10px rgba(0,0,0,0.6)",
                      "inset 0 1px 0 rgba(255,255,255,0.14)",
                    ].join(", "),
                  }}
                  whileHover={
                    prefersReduced
                      ? undefined
                      : {
                          scale: 1.022,
                          boxShadow: [
                            "0 0 0 1.5px rgba(114,137,218,0.7)",
                            "0 8px 40px rgba(88,101,242,0.65)",
                            "0 0 80px rgba(88,101,242,0.25)",
                            "inset 0 1px 0 rgba(255,255,255,0.18)",
                          ].join(", "),
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 16,
                          },
                        }
                  }
                  whileTap={
                    prefersReduced
                      ? undefined
                      : {
                          scale: 0.972,
                          transition: {
                            type: "spring",
                            stiffness: 600,
                            damping: 22,
                          },
                        }
                  }
                >
                  {/* Top-edge glow border */}
                  <div
                    className="absolute left-0 top-0 w-full h-full p-[1px] pointer-events-none rounded-xl"
                    style={{
                      background:
                        "radial-gradient(65% 80% at 50% 0%, rgba(165,180,252,0.9) 0%, rgba(88,101,242,0.4) 60%, transparent 100%)",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />
                  <DiscordLogo size={22} />
                  <span
                    className="bebas-neue-font text-white text-[1.45rem] tracking-[0.06em]"
                    style={{
                      position: "relative",
                      textShadow: "0 1px 6px rgba(0,0,0,0.35)",
                    }}
                  >
                    Conectează Discord
                  </span>
                </motion.button>

                {/* Footer */}
                <div className="flex items-center justify-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <rect
                        x="2"
                        y="5"
                        width="8"
                        height="6"
                        rx="1"
                        stroke="rgba(107,111,133,0.6)"
                        strokeWidth="1.2"
                      />
                      <path
                        d="M4 5V3.5a2 2 0 014 0V5"
                        stroke="rgba(107,111,133,0.6)"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span
                      className="chakra-petch-font text-[0.6rem]"
                      style={{ color: C.muted, opacity: 0.65 }}
                    >
                      Nu stocăm parola
                    </span>
                  </div>
                  <div
                    className="w-[3px] h-[3px] rounded-full"
                    style={{ background: C.faint }}
                  />
                  <div className="flex items-center gap-1.5">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <circle
                        cx="6"
                        cy="6"
                        r="4.5"
                        stroke="rgba(107,111,133,0.6)"
                        strokeWidth="1.2"
                      />
                      <path
                        d="M6 3.5v2.5l1.5 1.5"
                        stroke="rgba(107,111,133,0.6)"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span
                      className="chakra-petch-font text-[0.6rem]"
                      style={{ color: C.muted, opacity: 0.65 }}
                    >
                      OAuth2 securizat
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (typeof window === "undefined") return null;
  return <ModalPortal>{content}</ModalPortal>;
}
